import React, { useState, useEffect } from 'react';
import { SystemConfig, School, LearningPhaseProgressionRule } from '../../data/types';
import { dataService } from '../../services/dataService';

interface LearningProgressionTabProps {
  config: SystemConfig;
  onSave: (updatedConfig: Partial<SystemConfig>) => void;
  saving: boolean;
}

const LearningProgressionTab: React.FC<LearningProgressionTabProps> = ({ config, onSave, saving }) => {
  const [districtRule, setDistrictRule] = useState<LearningPhaseProgressionRule>(
    config.learningPhaseProgression.districtDefault
  );
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string | null>(null);
  const [schoolRules, setSchoolRules] = useState<Record<string, LearningPhaseProgressionRule>>(
    config.learningPhaseProgression.schoolOverrides
  );
  const [loading, setLoading] = useState(false);
  const [previewResults, setPreviewResults] = useState<{
    passesVideo: boolean;
    passesPractice: boolean;
    overallResult: boolean;
  } | null>(null);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoading(true);
        const schoolsData = await dataService.getSchools();
        setSchools(schoolsData);
      } catch (error) {
        console.error('Error fetching schools:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  const handleSave = () => {
    const updatedConfig: Partial<SystemConfig> = {
      learningPhaseProgression: {
        districtDefault: districtRule,
        schoolOverrides: schoolRules
      }
    };
    
    onSave(updatedConfig);
  };

  const handleAddSchoolOverride = () => {
    if (!selectedSchool) return;
    
    // Create a new rule based on the district default
    const newRule: LearningPhaseProgressionRule = {
      ...districtRule,
      id: `progression-rule-${selectedSchool}`,
      name: `Custom Rule for ${schools.find(s => s.id === selectedSchool)?.name || 'School'}`,
      isDefault: false
    };
    
    setSchoolRules({
      ...schoolRules,
      [selectedSchool]: newRule
    });
    
    setSelectedSchool(null);
  };

  const handleRemoveSchoolOverride = (schoolId: string) => {
    const newRules = { ...schoolRules };
    delete newRules[schoolId];
    setSchoolRules(newRules);
  };

  const updateDistrictRule = (field: keyof LearningPhaseProgressionRule, value: any) => {
    setDistrictRule({
      ...districtRule,
      [field]: value
    });
  };

  const updateSchoolRule = (schoolId: string, field: keyof LearningPhaseProgressionRule, value: any) => {
    setSchoolRules({
      ...schoolRules,
      [schoolId]: {
        ...schoolRules[schoolId],
        [field]: value
      }
    });
  };

  const simulateProgression = (rule: LearningPhaseProgressionRule) => {
    // Mock values for simulation
    const videoCompletion = 85; // 85%
    const practiceAccuracy = 75; // 75%
    const questionsCompleted = 4;
    
    // Check if passes video requirement
    const passesVideo = !rule.videoCompletionRequired || 
      videoCompletion >= rule.minVideoCompletionPercentage;
    
    // Check if passes practice requirement
    const passesPractice = !rule.practiceQuestionsRequired || 
      (questionsCompleted >= rule.minPracticeQuestions && 
       practiceAccuracy >= rule.minAccuracyPercentage);
    
    // Overall result
    const overallResult = passesVideo && passesPractice;
    
    setPreviewResults({
      passesVideo,
      passesPractice,
      overallResult
    });
  };

  // Render a rule editor (reused for district and school rules)
  const renderRuleEditor = (
    rule: LearningPhaseProgressionRule, 
    onChange: (field: keyof LearningPhaseProgressionRule, value: any) => void,
    showSimulate: boolean = true,
    schoolId?: string
  ) => (
    <div className="border border-gray-200 rounded-lg p-4 mb-4">
      <h3 className="font-semibold text-gray-700 mb-3">{rule.name}</h3>
      
      {/* Video Completion Settings */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id={`video-required-${rule.id}`}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            checked={rule.videoCompletionRequired}
            onChange={(e) => onChange('videoCompletionRequired', e.target.checked)}
          />
          <label htmlFor={`video-required-${rule.id}`} className="ml-2 block text-sm font-medium text-gray-700">
            Require video completion
          </label>
        </div>
        
        {rule.videoCompletionRequired && (
          <div className="ml-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum video completion percentage
            </label>
            <div className="flex items-center">
              <input
                type="range"
                min="50"
                max="100"
                step="5"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                value={rule.minVideoCompletionPercentage}
                onChange={(e) => onChange('minVideoCompletionPercentage', parseInt(e.target.value))}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                {rule.minVideoCompletionPercentage}%
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Practice Questions Settings */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id={`practice-required-${rule.id}`}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            checked={rule.practiceQuestionsRequired}
            onChange={(e) => onChange('practiceQuestionsRequired', e.target.checked)}
          />
          <label htmlFor={`practice-required-${rule.id}`} className="ml-2 block text-sm font-medium text-gray-700">
            Require practice questions
          </label>
        </div>
        
        {rule.practiceQuestionsRequired && (
          <div className="ml-6 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum number of questions
              </label>
              <input
                type="number"
                min="1"
                max="20"
                className="w-24 border border-gray-300 rounded px-3 py-2"
                value={rule.minPracticeQuestions}
                onChange={(e) => onChange('minPracticeQuestions', parseInt(e.target.value) || 5)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum accuracy percentage
              </label>
              <div className="flex items-center">
                <input
                  type="range"
                  min="50"
                  max="100"
                  step="5"
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  value={rule.minAccuracyPercentage}
                  onChange={(e) => onChange('minAccuracyPercentage', parseInt(e.target.value))}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {rule.minAccuracyPercentage}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Simulation and Actions */}
      <div className="flex justify-between items-center mt-4">
        {showSimulate && (
          <button
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            onClick={() => simulateProgression(rule)}
          >
            Simulate Progression
          </button>
        )}
        
        {schoolId && (
          <button
            className="px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100"
            onClick={() => handleRemoveSchoolOverride(schoolId)}
          >
            Remove Override
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">Learning Phase Progression Configuration</h2>
      <p className="text-gray-600 mb-6">
        Configure the rules that determine when students can progress from one learning phase to the next.
        These rules control when students can move from watching instructional videos to practice questions,
        and finally to playing games.
      </p>
      
      {/* District Default Rule */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">District Default Rule</h3>
        <p className="text-sm text-gray-500 mb-3">
          This rule applies to all schools unless they have a specific override
        </p>
        
        {renderRuleEditor(districtRule, updateDistrictRule)}
      </div>
      
      {/* Simulation Results */}
      {previewResults && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Simulation Results</h3>
          <div className="space-y-2">
            <div className="flex items-center">
              <span className={`inline-block w-5 h-5 rounded-full ${previewResults.passesVideo ? 'bg-green-500' : 'bg-red-500'} mr-2`}></span>
              <span className="text-sm">
                Video Completion: {previewResults.passesVideo ? 'Passed' : 'Failed'}
              </span>
            </div>
            <div className="flex items-center">
              <span className={`inline-block w-5 h-5 rounded-full ${previewResults.passesPractice ? 'bg-green-500' : 'bg-red-500'} mr-2`}></span>
              <span className="text-sm">
                Practice Questions: {previewResults.passesPractice ? 'Passed' : 'Failed'}
              </span>
            </div>
            <div className="flex items-center font-medium">
              <span className={`inline-block w-5 h-5 rounded-full ${previewResults.overallResult ? 'bg-green-500' : 'bg-red-500'} mr-2`}></span>
              <span className="text-sm">
                Overall Result: {previewResults.overallResult ? 'Student can progress' : 'Student cannot progress'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Simulation based on: 85% video completion, 75% practice accuracy, 4 questions completed
            </p>
          </div>
        </div>
      )}
      
      {/* School-Specific Overrides */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">School-Specific Overrides</h3>
        <p className="text-sm text-gray-500 mb-3">
          Configure custom progression rules for specific schools
        </p>
        
        {/* Existing School Overrides */}
        {Object.entries(schoolRules).map(([schoolId, rule]) => {
          const school = schools.find(s => s.id === schoolId);
          return (
            <div key={schoolId} className="mb-4">
              <div className="flex items-center mb-2">
                {school?.logo ? (
                  <img src={school.logo} alt={school.name} className="h-8 w-8 rounded-full mr-2" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                    <span className="text-gray-600 text-xs font-bold">
                      {school?.name.substring(0, 2).toUpperCase() || 'SC'}
                    </span>
                  </div>
                )}
                <span className="text-sm font-medium text-gray-700">
                  {school?.name || 'Unknown School'}
                </span>
              </div>
              
              {renderRuleEditor(
                rule, 
                (field, value) => updateSchoolRule(schoolId, field, value),
                false,
                schoolId
              )}
            </div>
          );
        })}
        
        {/* Add New School Override */}
        <div className="mt-4 p-4 border border-dashed border-gray-300 rounded-lg">
          <h4 className="font-medium text-gray-700 mb-2">Add School Override</h4>
          <div className="flex items-center">
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 mr-2"
              value={selectedSchool || ''}
              onChange={(e) => setSelectedSchool(e.target.value || null)}
            >
              <option value="">Select a school...</option>
              {schools
                .filter(school => !(school.id in schoolRules))
                .map(school => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))
              }
            </select>
            <button
              className={`px-3 py-2 rounded ${
                selectedSchool
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              onClick={handleAddSchoolOverride}
              disabled={!selectedSchool}
            >
              Add
            </button>
          </div>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className={`px-4 py-2 rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
          }`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Progression Rules'}
        </button>
      </div>
    </div>
  );
};

export default LearningProgressionTab;
